import { useState } from "react";
import {
    MessageSquare,
    Paperclip,
    Clock,
    AlertCircle,
    CheckCircle2,
    MoreVertical,
    User,
    Calendar,
    Tag,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Ticket {
    id: number;
    ticket_number: string;
    subject: string;
    status: string;
    priority: string;
    client_id: number;
    client_name: string;
    created_by: number;
    created_by_name: string;
    assigned_to: number | null;
    assigned_to_name: string | null;
    created_at: string;
    updated_at: string;
    first_response_at: string | null;
    messages_count: number;
    attachments_count: number;
    time_spent: number;
    days_open: number;
}

interface TicketListAdminProps {
    tickets: Ticket[];
    isLoading?: boolean;
    currentUserId?: number;
    onAction: (action: string, ticketId: number) => void;
    onAssign: (ticketId: number, userId: number | null) => void;
}

const statusColors: Record<string, { bg: string; text: string; icon: any }> = {
    open: { bg: "bg-red-100", text: "text-red-800", icon: AlertCircle },
    novo: { bg: "bg-red-100", text: "text-red-800", icon: AlertCircle },
    assigned: { bg: "bg-blue-100", text: "text-blue-800", icon: User },
    atribuido: { bg: "bg-blue-100", text: "text-blue-800", icon: User },
    em_analise: { bg: "bg-indigo-100", text: "text-indigo-800", icon: Clock },
    in_progress: { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock },
    em_atendimento: { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock },
    waiting_client: { bg: "bg-purple-100", text: "text-purple-800", icon: MessageSquare },
    aguardando_cliente: { bg: "bg-purple-100", text: "text-purple-800", icon: MessageSquare },
    waiting_client_info: { bg: "bg-orange-100", text: "text-orange-800", icon: AlertCircle },
    aguardando_validacao: { bg: "bg-amber-100", text: "text-amber-800", icon: Clock },
    resolved: { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle2 },
    resolvido: { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle2 },
    closed: { bg: "bg-gray-100", text: "text-gray-800", icon: CheckCircle2 },
    fechado: { bg: "bg-gray-100", text: "text-gray-800", icon: CheckCircle2 },
    cancelled: { bg: "bg-slate-100", text: "text-slate-800", icon: AlertCircle },
};

const priorityColors: Record<string, string> = {
    low: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    high: "bg-orange-100 text-orange-800",
    urgent: "bg-red-100 text-red-800",
};

const statusLabels: Record<string, string> = {
    open: "Aberto",
    novo: "Novo",
    assigned: "Atribuído",
    atribuido: "Atribuído",
    em_analise: "Em Análise",
    in_progress: "Em Progresso",
    em_atendimento: "Em Atendimento",
    waiting_client: "Aguardando Cliente",
    aguardando_cliente: "Aguardando Cliente",
    waiting_client_info: "Aguardando Info",
    aguardando_validacao: "Aguardando Validação",
    resolved: "Resolvido",
    resolvido: "Resolvido",
    closed: "Fechado",
    fechado: "Fechado",
    cancelled: "Cancelado",
};

const priorityLabels: Record<string, string> = {
    low: "Baixa",
    medium: "Média",
    high: "Alta",
    urgent: "Urgente",
};

export const TicketListAdmin = ({
    tickets,
    isLoading,
    currentUserId,
    onAction,
    onAssign
}: TicketListAdminProps) => {
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDateOnly = (date: string) => {
        return new Date(date).toLocaleDateString("pt-BR");
    };

    const getActionButtons = (ticket: Ticket) => {
        const actions = [];

        // Status: novo ou open (não atribuído)
        if (ticket.status === "novo" || ticket.status === "open") {
            // Se não tem assigned_to, mostrar botão Atribuir
            if (!ticket.assigned_to) {
                actions.push(
                    { label: "Atribuir", action: "assign", variant: "outline" },
                    { label: "Ver", action: "view", variant: "ghost" }
                );
            } else if (currentUserId && ticket.assigned_to === currentUserId) {
                // Se o responsável é o usuário logado, mostrar Iniciar
                actions.push(
                    { label: "Iniciar", action: "start", variant: "default" },
                    { label: "Ver", action: "view", variant: "ghost" }
                );
            } else {
                // Responsável é outro usuário: mostrar Transferir (para admin) ou apenas Ver
                actions.push(
                    { label: "Transferir", action: "transfer", variant: "outline" },
                    { label: "Ver", action: "view", variant: "ghost" }
                );
            }
        } 
        // Status: em_atendimento ou in_progress
        else if (ticket.status === "em_atendimento" || ticket.status === "in_progress") {
            // Apenas o responsável pode responder/fechar
            if (currentUserId && ticket.assigned_to === currentUserId) {
                actions.push(
                    { label: "Responder", action: "respond", variant: "default" },
                    { label: "Pedir Info", action: "ask_info", variant: "outline" },
                    { label: "Fechar", action: "close", variant: "destructive" }
                );
            } else {
                actions.push(
                    { label: "Ver", action: "view", variant: "ghost" }
                );
            }
        } 
        // Status: aguardando_resposta ou waiting_client
        else if (ticket.status === "aguardando_resposta" || ticket.status === "waiting_client") {
            actions.push(
                { label: "Ver", action: "view", variant: "ghost" }
            );
        }
        // Status: resolvido ou resolved
        else if (ticket.status === "resolvido" || ticket.status === "resolved") {
            actions.push(
                { label: "Ver", action: "view", variant: "ghost" },
                { label: "Reabrir", action: "reopen", variant: "outline" }
            );
        }
        // Status: fechado ou closed
        else if (ticket.status === "fechado" || ticket.status === "closed") {
            actions.push(
                { label: "Ver", action: "view", variant: "ghost" }
            );
        }
        // Fallback: sempre mostrar Ver
        else {
            actions.push(
                { label: "Ver", action: "view", variant: "ghost" }
            );
        }

        console.log('Ticket:', ticket.ticket_number, 'Status:', ticket.status, 'Actions:', actions);
        return actions;
    };

    if (isLoading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="bg-gray-100 h-20 rounded-lg animate-pulse" />
                ))}
            </div>
        );
    }

    if (tickets.length === 0) {
        return (
            <div className="text-center py-12" data-testid="empty-state">
                <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500">Nenhum ticket encontrado com esses filtros</p>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="ticket-list">
            {tickets.map(ticket => {
                const isExpanded = expandedId === ticket.id;
                const statusColor = statusColors[ticket.status] || statusColors['open']; // Fallback para 'open'
                const StatusIcon = statusColor.icon;

                return (
                    <div
                        key={ticket.id}
                        data-testid="ticket-card"
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                        {/* Linha principal */}
                        <div className="p-4 flex items-center justify-between gap-4">
                            {/* Esquerda: Número e Assunto */}
                            <div className="flex-1 min-w-0">
                                <button
                                    onClick={() => onAction("view", ticket.id)}
                                    className="text-left hover:opacity-75 transition-opacity w-full"
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Status Badge */}
                                        <div className={`flex-shrink-0 ${statusColor.bg} rounded-lg p-2`}>
                                            <StatusIcon className={`w-4 h-4 ${statusColor.text}`} />
                                        </div>

                                        {/* Conteúdo */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                                                <span className="font-mono font-bold text-sm text-gray-900" data-testid="ticket-number">
                                                    {ticket.ticket_number}
                                                </span>
                                                <Badge 
                                                    className={`${priorityColors[ticket.priority]} border-0`}
                                                    data-testid="ticket-priority"
                                                >
                                                    {ticket.priority ? priorityLabels[ticket.priority] : "N/A"}
                                                </Badge>
                                            </div>
                                            <p className="font-semibold text-gray-900 truncate mb-1" data-testid="ticket-title">
                                                {ticket.subject}
                                            </p>
                                            <p className="text-sm text-gray-600 truncate" data-testid="ticket-client">
                                                {ticket.client_name}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            </div>

                            {/* Centro: Info rápida */}
                            <div className="hidden md:flex items-center gap-4 text-sm">
                                {/* Responsável */}
                                <div className="flex items-center gap-1.5 text-gray-700">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span>
                                        {ticket.assigned_to_name ? (
                                            <span className="font-medium text-gray-900">{ticket.assigned_to_name}</span>
                                        ) : (
                                            <span className="text-gray-500 italic">Sem atribuição</span>
                                        )}
                                    </span>
                                </div>

                                {/* Mensagens */}
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-gray-700">
                                    <MessageSquare className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium">{ticket.messages_count}</span>
                                </div>

                                {/* Tempo */}
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-gray-700">
                                    <Clock className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium">{ticket.days_open}d</span>
                                </div>
                            </div>

                            {/* Direita: Ações */}
                            <div className="flex items-center gap-2">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" data-testid="ticket-actions-menu" className="text-gray-700 hover:text-gray-900 hover:bg-gray-100">
                                            <MoreVertical className="w-5 h-5" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48" data-testid="actions-dropdown">
                                        {getActionButtons(ticket).map(btn => (
                                            <DropdownMenuItem
                                                key={btn.action}
                                                onClick={() => onAction(btn.action, ticket.id)}
                                                className="cursor-pointer"
                                            >
                                                {btn.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid="expand-ticket-btn"
                                    onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                                    className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 font-bold text-lg"
                                >
                                    {isExpanded ? "−" : "+"}
                                </Button>
                            </div>
                        </div>

                        {/* Seção expandida */}
                        {isExpanded && (
                            <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3" data-testid="ticket-details">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    {/* Status */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Status</label>
                                        <p className="mt-1 font-semibold text-gray-900" data-testid="ticket-status">
                                            {statusLabels[ticket.status]}
                                        </p>
                                    </div>

                                    {/* Criado por */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Criado por</label>
                                        <p className="mt-1 font-semibold text-gray-900">
                                            {ticket.created_by_name}
                                        </p>
                                    </div>

                                    {/* Data de Criação */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Data Criação</label>
                                        <p className="mt-1 font-semibold text-gray-900" data-testid="ticket-created-date">
                                            {formatDateOnly(ticket.created_at)}
                                        </p>
                                    </div>

                                    {/* Tempo Gasto */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Tempo Gasto</label>
                                        <p className="mt-1 font-semibold text-gray-900">
                                            {ticket.time_spent > 0 ? `${ticket.time_spent}min` : "0min"}
                                        </p>
                                    </div>

                                    {/* Responsável */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Responsável</label>
                                        <p className="mt-1 font-semibold text-gray-900">
                                            {ticket.assigned_to_name || <span className="text-gray-500 italic">Sem atribuição</span>}
                                        </p>
                                    </div>

                                    {/* Mensagens */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Mensagens</label>
                                        <p className="mt-1 font-semibold text-gray-900">{ticket.messages_count}</p>
                                    </div>

                                    {/* Anexos */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Anexos</label>
                                        <p className="mt-1 font-semibold text-gray-900">{ticket.attachments_count}</p>
                                    </div>

                                    {/* Primeira Resposta */}
                                    <div>
                                        <label className="text-gray-500 text-xs font-semibold uppercase tracking-wide">1ª Resposta</label>
                                        <p className="mt-1 font-semibold text-gray-900">
                                            {ticket.first_response_at
                                                ? formatDate(ticket.first_response_at)
                                                : <span className="text-gray-500">−</span>}
                                        </p>
                                    </div>
                                </div>

                                {/* Botões de Ação */}
                                <div className="flex gap-2 pt-3 border-t border-gray-200">
                                    {getActionButtons(ticket).map(btn => {
                                        // Definir cores específicas para cada variante
                                        let buttonClass = "font-medium ";
                                        if (btn.variant === "default") {
                                            buttonClass += "bg-blue-600 hover:bg-blue-700 text-white";
                                        } else if (btn.variant === "outline") {
                                            buttonClass += "border-2 border-gray-700 text-gray-900 bg-white hover:bg-gray-100";
                                        } else if (btn.variant === "destructive") {
                                            buttonClass += "bg-red-600 hover:bg-red-700 text-white";
                                        } else if (btn.variant === "ghost") {
                                            buttonClass += "text-gray-900 bg-gray-100 hover:bg-gray-200";
                                        }
                                        
                                        return (
                                            <Button
                                                key={btn.action}
                                                variant={btn.variant as any}
                                                size="sm"
                                                onClick={() => onAction(btn.action, ticket.id)}
                                                className={buttonClass}
                                            >
                                                {btn.label}
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
